import { Document, Page, Text, View, StyleSheet, PDFViewer, Image, Svg, Link } from '@react-pdf/renderer';
import { useEffect, useState } from 'react';
import { AlgoliaFinishHit, FinishFile } from '../types/AlgoliaTypes';
import { useLocation, useParams } from 'react-router-dom';
import { getFinishByRouterValue } from '../services/algolia';
import Table from '../components/PDF/Table';
import { productData } from '../data/products';
import { PLACEHOLDER_IMAGE } from '../data/constants';
import { DeliverAPIOptions } from '../components/layout/SteelcaseImage/SteelcaseImage';

const styles = StyleSheet.create({
    page: {
      flexDirection: 'row',
      backgroundColor: '#FFF',
      paddingBottom: 20
    },
    section: {
      margin: 10,
      padding: 10,
      flexGrow: 1,
    },
    familyName: {
        marginTop: 3,
        fontSize: "18pt",
        fontWeight: 700,
        fontStyle: "normal",
        lineHeight: 0.75,
        letterSpacing: -0.38,
        color: "#000",
        fontFamily: "Helvetica-Bold"
    },
    finishName: {
        marginTop: 8,
        fontSize: "6.75pt",
        color: "#4d4d4d"
    },
    imagesContainer: {
        width: "100%",
        marginTop: 15,
        marginBottom: 8,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
    },
    imageContainer: {

    },
    swatchImage: {
        marginTop: 4
    },
    swatchImageText: {
        fontSize: "6.5pt",
        color: "#4d4d4d",
        marginTop: 8
    },
    altFinishReferences: {
        color: "#4d4d4d",
        fontSize: "6.75pt",
        marginTop: 6
    },
    variantFinishName: {
        fontSize: "6.25pt",
        color: "#4d4d4d",
        height: 20,
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        alignContent: "center",
        marginTop: 8
    },
    variantImageListContainer: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        alignContent: "center",
        gap: "2"
    },
    disclaimer: {
        color: "#4d4d4d",
        fontSize: "6.75pt",
        fontFamily: "Helvetica-Bold",
        marginTop: 6
    },
    brand: {
        color: "#4d4d4d",
        fontSize: "6.75pt",
        fontFamily: "Helvetica-Bold"
    },
    footerLogo: {
        position: "absolute",
        bottom: -10,
        right: 0,
        width: "63pt",
        height: "12pt",
    },
    footerDisclaimer: {
        
    },
    lastFooterContainer: {
        fontFamily: "Helvetica",
        fontSize: "7pt",
        lineHeight: 1.25,
        color: "#4d4d4d",
        display: "flex"
    }
});

export default function SpecSheet() {
    const { finish_code } = useParams();
    
    const [finishData, setFinishData] = useState<AlgoliaFinishHit>();
    const [totalPages, setTotalPages] = useState<number>();

    const retrieveObj = async () => {
        if(finish_code) {
            const finishDataObj = await getFinishByRouterValue(finish_code)
            setFinishData(finishDataObj);
        }
    }

    function getGreyscaleImage(finishFiles: FinishFile[]): string {
        const greyscaleFile = finishFiles.find(file => file.fileType === "greyscale_image");
        if(greyscaleFile && greyscaleFile.fileURL) return applyOptions(greyscaleFile?.fileURL, {seoFileName: greyscaleFile.fileName, format: "jpeg", width: "1000", height: "1000", quality: "100", crop: {cropType: "absolute", cropValues: "0,0,500,500"}});
        return PLACEHOLDER_IMAGE;
    }

    const applyOptions = (transformedUrl: string, deliveryOptions: Partial<DeliverAPIOptions>): string => {
        if(transformedUrl.includes("delivery")) {
            if(deliveryOptions.original) {
                return `${transformedUrl}/original/as/${deliveryOptions.seoFileName}`
            }
            if(deliveryOptions.seoFileName && deliveryOptions.format) {
                transformedUrl += `/as/${deliveryOptions.seoFileName}.${deliveryOptions.format}`
            }
    
            transformedUrl = transformedUrl += "?";
    
            if(deliveryOptions.width) transformedUrl += `width=${deliveryOptions.width}&`;
    
            if(deliveryOptions.height) transformedUrl += `height=${deliveryOptions.height}&`;
            
            if(deliveryOptions.quality) transformedUrl += `quality=${deliveryOptions.quality}&`;
            
    
            if(deliveryOptions.crop && deliveryOptions.crop.cropType === "percentage" && deliveryOptions.crop.cropValues) transformedUrl += `crop=${deliveryOptions.crop.cropValues}&`;
            
        
            if(deliveryOptions.crop && deliveryOptions.crop.cropType === "absolute" && deliveryOptions.crop.cropValues) transformedUrl += `crop=${deliveryOptions.crop.cropValues}&`;
    
            if(transformedUrl.endsWith('&')) {
                transformedUrl = transformedUrl.slice(0, -1);
            }
        
            if(transformedUrl.endsWith('?')) {
                transformedUrl = transformedUrl.slice(0, -1);
            }
        }
        return transformedUrl;
    }

    useEffect(() => {
        retrieveObj();
    }, [finish_code])

    return (
        <div>
            <PDFViewer style={{ width: '100%', height: '100vh' }} showToolbar={true}>
                <Document>
                    <Page size={"A4"} style={styles.page}>
                        <View style={styles.section}>
                            <View render={({pageNumber}) => (
                                pageNumber !== 1 && 
                                <View style={{ marginVertical: 10,
                                    paddingVertical: 10, }}>
                                    <Text style={[styles.brand, { fontSize: "7.5pt" }]}>
                                        {finishData &&
                                            finishData.brandNames && finishData.brandNames[0] ?
                                                finishData.brandNames[0] :
                                                ""
                                        }
                                    </Text>
                                    <Text style={[styles.familyName, { fontSize: "9pt", marginTop: 1 }]}>
                                        {finishData && 
                                            finishData.parentFabricFamily && 
                                            finishData.parentFabricFamily.familyName ? 
                                                finishData.parentFabricFamily.familyName :
                                                finishData && finishData.finishName ? finishData.finishName : ""
                                        }
                                    </Text>
                                </View>
                            )} fixed/>
                            <Text style={styles.brand}>
                                {finishData &&
                                    finishData.brandNames && finishData.brandNames[0] ?
                                            finishData.brandNames[0] :
                                            ""
                                }
                            </Text>
                            {/* Family Name */}
                            <Text style={styles.familyName}>
                                {finishData && 
                                    finishData.parentFabricFamily && 
                                    finishData.parentFabricFamily.familyName ? 
                                        finishData.parentFabricFamily.familyName :
                                        finishData && finishData.finishName ? finishData.finishName : ""
                                } 
                            </Text>
                            {/* Finish Name */}
                            <Text style={styles.finishName}>{finishData?.finishName ?? ""}</Text>
                            {/* Finish References */}
                            {
                                finishData && finishData.alternateFinishReferences ? 
                                    finishData.alternateFinishReferences.map((item, idx) => {
                                        return (
                                            <Text key={`${item}-${idx}`} style={styles.altFinishReferences}>{item}</Text>
                                        )
                                    })
                                : null
                            }
                            {/* Disclaimer */}
                            <Text style={styles.disclaimer}>{window.steelcase_i18n['steelcase-finish-library'].materials_text || "Materials are displayed for reference and may vary by screen and print quality. Please refer to a physical sample for actual color and detail"}</Text>
                            <View style={[styles.imagesContainer, {flexGrow: 1}]}>
                            
                                <View style={{ width: "48%" }}>
                                    <Image src={finishData && finishData.swatch ? applyOptions(finishData.swatch.fileURL, {seoFileName: finishData.finishName, format: "jpeg", width: "1000", height: "1000", crop: {cropType: "absolute", cropValues: "500,500,500,500"}})  : PLACEHOLDER_IMAGE} style={[styles.swatchImage, {width: "100%"}]}/>
                                    <Text style={styles.swatchImageText}>{window.steelcase_i18n['steelcase-finish-library'].image_shown || "Image shown: "} {finishData?.finishName ?? ""}</Text>
                                </View>
                                <View style={{ width: "48%" }}>
                                    {
                                        finishData && finishData.parentFabricFamily ?
                                        <>
                                            <Image src={finishData && finishData.parentFabricFamily && getGreyscaleImage(finishData?.parentFabricFamily.finishFiles)} style={[styles.swatchImage, {width: "100%"}]}/>
                                            <Text style={styles.swatchImageText}>{window.steelcase_i18n["steelcase-finish-library"].repeat_upholstery || "Repeat"}</Text>
                                        </>
                                        : null
                                    }
                                </View>
                                    
                            </View>
                            
                            <View style={[styles.variantImageListContainer, { width: "100%" }]}>
                                {
                                    finishData && finishData.parentFabricFamily && finishData.variants ? finishData?.variants?.map((variant, idx) => {
                                        return (
                                            <View style={{ width: "22%" }} key={`${variant.finishName}-${idx}`}>
                                                <Image src={variant?.swatchURL} style={[styles.swatchImage, { width: "100%", height: "45pt" }]} />
                                                <Text style={styles.variantFinishName}>{`${variant.finishName} – ${variant.finishCode}`}</Text>
                                            </View>
                                        )
                                    }):
                                    null
                                }
                            </View>
                            
                            
                            {/* Workaround to have totalPages available out of Text component */}
                            <Text render={({totalPages}) => {
                                setTotalPages(totalPages)
                                return null;
                            }}/>

                            {/* Footer */}
                            <View style={styles.footerLogo} fixed render={({pageNumber}) => (
                                pageNumber !== totalPages &&
                                <Image style={{ width: "63pt", height: "12pt" }} src={"https://dumy1g3ng547g.cloudfront.net/content/themes/steelcase/img/logo-150.png"} />
                            )} />
                            
                            {/* Last Page Footer */}
                            <View style={[{ position: "absolute", bottom: -10, left: 0 }, styles.lastFooterContainer]} fixed render={({pageNumber}) => (
                                pageNumber === totalPages && (
                                    <View>
                                        <Image style={{ width: "63pt", height: "12pt" }} src={"https://dumy1g3ng547g.cloudfront.net/content/themes/steelcase/img/logo-150.png"} />
                                        <Text style={{ fontSize: "9pt", fontFamily: "Helvetica-Bold", color: "#000000", paddingTop: "6pt" }}>Visit <Link style={{ textDecoration: "none" }} href={"https://steelcase.com"}>Steelcase.com</Link></Text>
                                        <Text>
                                        ©2020 Steelcase Inc. All rights reserved. All specifications subject to change without notice.
                                        Trademarks used herein are the property of Steelcase Inc. or of their respective owners.
                                        </Text>
                                    </View>
                                )
                            )} />
                        </View>
                    </Page>
                    {/* Tabs section */}
                    <Page size={"A4"} style={[styles.page]}>
                        <View style={styles.section}>
                            <View render={({pageNumber}) => (
                                pageNumber !== 1 && 
                                    <View style={{ paddingTop: 16, paddingBottom: 10 }}>
                                        <Text style={[styles.brand, { fontSize: "7.5pt" }]}>Steelcase</Text>
                                        <Text style={[styles.familyName, { fontSize: "9pt", marginTop: 1 }]}>
                                        {finishData && 
                                            finishData.parentFabricFamily && 
                                            finishData.parentFabricFamily.familyName ? 
                                                finishData.parentFabricFamily.familyName :
                                                finishData && finishData.finishName ? finishData.finishName : ""
                                        }
                                        </Text>
                                    </View>
                            )} fixed/>

                            {/* Tables */}
                            <View style={{ display: "flex", gap: 10, padding: 10 }}>
                            {
                                finishData?.tabs && finishData.tabs.map((tab, idx) => {
                                    return <Table key={idx} data={tab} />
                                })
                            }
                            </View>
                            {/* Footer */}
                            <View style={styles.footerLogo} fixed render={({pageNumber}) => (
                                pageNumber !== totalPages &&
                                <Image style={{ width: "63pt", height: "12pt" }} src={"https://dumy1g3ng547g.cloudfront.net/content/themes/steelcase/img/logo-150.png"} />
                            )} />
                            
                            {/* Last Page Footer */}
                            <View style={[{ position: "absolute", bottom: -10, left: 0, paddingBottom: 20 }, styles.lastFooterContainer]} fixed render={({pageNumber}) => (
                                pageNumber === totalPages && (
                                    <View style={{ width: "50%" }}>
                                        <Image style={{ width: "63pt", height: "12pt" }} src={"https://dumy1g3ng547g.cloudfront.net/content/themes/steelcase/img/logo-150.png"} />
                                        <Text style={{ fontSize: "9pt", fontFamily: "Helvetica-Bold", color: "#000000", paddingTop: "6pt" }}>Visit <Link style={{ textDecoration: "none" }} href={"https://steelcase.com"}>Steelcase.com</Link></Text>
                                        <Text>
                                        ©2020 Steelcase Inc. All rights reserved. All specifications subject to change without notice.
                                        Trademarks used herein are the property of Steelcase Inc. or of their respective owners.
                                        </Text>
                                    </View>
                                )
                            )} />
                        </View>
                    </Page>
                    
                    {
                        finishData && finishData.filters && finishData.filters.productLineAvailability ?
                    <Page size={"A4"} style={[styles.page]}>
                        <View style={styles.section}>
                            <View render={({pageNumber}) => (
                                pageNumber !== 1 && 
                                    <View style={{ paddingTop: 16, paddingBottom: 10 }}>
                                        <Text style={[styles.brand, { fontSize: "7.5pt" }]}>
                                            {finishData &&
                                                finishData.brandNames && finishData.brandNames[0] ?
                                                        finishData.brandNames[0] :
                                                        ""
                                            }      
                                        </Text>
                                        <Text style={[styles.familyName, { fontSize: "9pt", marginTop: 1 }]}>
                                            {finishData && 
                                                finishData.parentFabricFamily && 
                                                finishData.parentFabricFamily.familyName ? 
                                                    finishData.parentFabricFamily.familyName :
                                                    finishData && finishData.finishName ? finishData.finishName : ""
                                            }
                                        </Text>

                                        <Text style={[styles.familyName, { fontSize: "7.5pt", marginTop: 6, paddingBottom: 6, borderBottomWidth: 1 }]}>{window.steelcase_i18n["steelcase-finish-library"].configurator.full_availability.toUpperCase() || "Full Availability".toUpperCase}</Text>
                                    </View>
                            )} fixed/>

                            {/* Tables */}
                            <View style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 10, width: "100%" }}>
                            {
                                finishData?.filters?.productLineAvailability?.map((product, idx) => {
                                    return (
                                        <View key={idx} style={{ width: "22%" }}><Text key={idx} style={{ fontSize: "6.75pt", color: "#4d4d4d", lineHeight: 1.33}}>{product}</Text></View>
                                    )
                                })
                                
                            }
                            </View>
                            {/* Footer */}
                            <View style={styles.footerLogo} fixed render={({pageNumber}) => (
                                pageNumber !== totalPages &&
                                <Image style={{ width: "63pt", height: "12pt" }} src={"https://dumy1g3ng547g.cloudfront.net/content/themes/steelcase/img/logo-150.png"} />
                            )} />
                            
                            {/* Last Page Footer */}
                            <View style={[{ position: "absolute", bottom: -10, left: 0 }, styles.lastFooterContainer]} fixed render={({pageNumber}) => (
                                pageNumber === totalPages && (
                                    <View style={{ width: "50%" }}>
                                        <Image style={{ width: "63pt", height: "12pt" }} src={"https://dumy1g3ng547g.cloudfront.net/content/themes/steelcase/img/logo-150.png"} />
                                        <Text style={{ fontSize: "9pt", fontFamily: "Helvetica-Bold", color: "#000000", paddingTop: "6pt" }}>Visit <Link style={{ textDecoration: "none" }} href={"https://steelcase.com"}>Steelcase.com</Link></Text>
                                        <Text>
                                        ©2020 Steelcase Inc. All rights reserved. All specifications subject to change without notice.
                                        Trademarks used herein are the property of Steelcase Inc. or of their respective owners.
                                        </Text>
                                    </View>
                                )
                            )} />
                        </View>
                    </Page> : null
                    }
                </Document>
            </PDFViewer>
        </div>
    )
}

const getImageFromUrl = (url: string) => {
    return {
         uri: url,
        method: "GET",
        headers: {
            "Access-Control-Allow-Origin": "*",
            "Cache-Control": "no-cache",
        },
    } as any
}